import { getUserProfile } from 'helpers/userProfile';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { XIcon } from '~/icons';
import { useFilterContext } from '../../contexts/FilterContext';
import { canViewThermalComfort } from '../../helpers/thermalComfort';
import { FilterOptions, ViewOption } from '../../types';
import { Option, Select } from '../SelectField';
import { ContainerClearFilters } from '../SelectField/styles';
import { healthComfortOptions, statusComfortOptions, statusHealthOptions } from './constants';

interface FilterFieldsProps {
  filterOptions: FilterOptions;
  disabled: boolean;
}

export function FilterFields({
  filterOptions,
}: Readonly<FilterFieldsProps>): ReactElement | null {
  const [profile] = useState(getUserProfile);
  const {
    clients,
    onSelectClient,
    onSelectOption,
    onSelectView,
    clearAllFilters,
    selectedOptions,
    viewOption,
    clearClients,
  } = useFilterContext();
  const { t } = useTranslation();

  const hasClientInput = !!(profile.manageAllClients || profile.manageMultipleClients);
  const hasSelectedOptionsClear = (selectedOptions.group || selectedOptions.state || selectedOptions.status || selectedOptions.healthStatus || selectedOptions.cities || selectedOptions.units) || (hasClientInput && clients.length > 0);

  function onClickClearButton(): void {
    clearAllFilters();
    if (hasClientInput) {
      clearClients();
    }
  }

  function onSelectGroup(value: string): void {
    const units = filterOptions.groups.find((item) => item.value === value)?.units ?? [];

    onSelectOption('group', value, units);
  }

  function getViewOptions(): Option[] {
    if (canViewThermalComfort(profile)) {
      return healthComfortOptions;
    }

    return healthComfortOptions.map(
      (item) => (item.value === 'thermalComfort' ? { ...item, label: t('confortoTermicoEmBreve'), comingSoon: true } : item),
    );
  }

  return (
    <>
      {hasClientInput && (
        <Select
          options={filterOptions.clients}
          placeholder={t('selecionarCliente')}
          value={clients}
          icon="User1Icon"
          onSelect={onSelectClient}
          multiple
          isSearchable
        />
      )}
      {clients.length > 0 && (
        <>
          <Select
            icon={viewOption === 'health' ? 'ActivityIcon' : 'Thermometer2Icon'}
            options={getViewOptions()}
            placeholder=""
            value={viewOption}
            onSelect={(value) => onSelectView(value as ViewOption)}
          />
          <Select
            icon="BookmarkIcon"
            options={
              viewOption === 'health'
                ? statusHealthOptions
                : statusComfortOptions
            }
            placeholder={t('selecionarStatus')}
            value={viewOption === 'health' ? selectedOptions.healthStatus : selectedOptions.status}
            onSelect={(value) => onSelectOption(viewOption === 'health' ? 'healthStatus' : 'status', value)}
          />
          <Select
            icon="MarkerPin1Icon"
            options={filterOptions.states}
            placeholder={t('selecionarEstado')}
            value={selectedOptions.state}
            onSelect={(value) => onSelectOption('state', value)}
            multiple
            isSearchable
          />
          <Select
            icon="MarkerPin1Icon"
            options={filterOptions.cities}
            placeholder={t('selecioneAsCidades')}
            value={selectedOptions.cities?.map((item) => item.value)}
            onSelect={(value) => onSelectOption('cities', value)}
            multiple
            isSearchable
          />
          {!profile.manageAllClients && filterOptions.groups.length > 0 && (
            <Select
              icon="LayoutGrid1Icon"
              options={filterOptions.groups}
              placeholder={t('selecionarGrupo')}
              value={selectedOptions.group?.value}
              onSelect={onSelectGroup}
            />
          )}
          <Select
            icon="LayoutGrid1Icon"
            options={filterOptions.units}
            placeholder={t('selecioneAsUnidades')}
            value={selectedOptions.units?.map((item) => item.value)}
            onSelect={(value) => onSelectOption('units', value)}
            multiple
            isSearchable
          />
        </>
      )}
      {
        hasSelectedOptionsClear && (
          <ContainerClearFilters onClick={onClickClearButton}>
            <XIcon />
          </ContainerClearFilters>
        )
      }
    </>
  );
}
